import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getStoredUser, logout } from "@/lib/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { LogOut } from "lucide-react";
import type { AuthUser } from "@/lib/types";
import { useLanguage } from "@/i18n";

export type Crumb = { label: string; href?: string };

/**
 * TopBar — breadcrumb nav + user account dropdown.
 *
 * The old dropdown was a bespoke flyout managed with useRef + document event
 * listeners. Replaced with shadcn's DropdownMenu (Radix primitive), which
 * provides keyboard navigation, focus management, and screen-reader semantics
 * for free.
 *
 * The search input that used to live here was removed: it had no handler and
 * silently discarded all input. Real cross-entity search is a feature, not a
 * styling pass.
 */
export default function TopBar({ crumbs = [], actions }: { crumbs?: Crumb[]; actions?: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const { t, lang, setLang } = useLanguage();

  useEffect(() => {
    setUser(getStoredUser());
  }, []);

  const initials = user
    ? (user.full_name || user.username)
        .split(/\s+/)
        .map((p) => p[0])
        .slice(0, 2)
        .join("")
        .toUpperCase()
    : "?";

  return (
    <header className="relative z-10 flex h-14 flex-none items-center justify-between gap-4 border-b border-line bg-surface/85 px-5 backdrop-blur">
      {/* Breadcrumb */}
      <nav aria-label={t.topBar.breadcrumb} className="flex min-w-0 items-center gap-2 font-display text-[15px] font-semibold uppercase tracking-[0.06em]">
        {crumbs.map((c, i) => {
          const last = i === crumbs.length - 1;
          return (
            <span key={`${c.label}-${i}`} className="flex min-w-0 items-center gap-1.5">
              {i > 0 && <span className="text-signal">/</span>}
              {c.href && !last ? (
                <Link to={c.href} className="truncate text-ink-muted transition hover:text-ink">
                  {c.label}
                </Link>
              ) : (
                <span
                  className={last ? "truncate text-ink" : "truncate text-ink-muted"}
                >
                  {c.label}
                </span>
              )}
            </span>
          );
        })}
      </nav>

      <div className="flex items-center gap-3">
        {actions && <div className="hidden items-center gap-2 lg:flex">{actions}</div>}
        {/* Language toggle */}
        <div
          role="group"
          aria-label={t.topBar.language}
          className="flex items-center rounded-full border border-line bg-surface p-0.5 text-2xs"
        >
          <button
            type="button"
            onClick={() => setLang("en")}
            aria-pressed={lang === "en"}
            className={`rounded-full px-2.5 py-1 font-medium transition ${
              lang === "en"
                ? "bg-accent-solid text-white"
                : "text-ink-muted hover:text-ink"
            }`}
          >
            English
          </button>
          <button
            type="button"
            onClick={() => setLang("mr")}
            aria-pressed={lang === "mr"}
            className={`rounded-full px-2.5 py-1 font-medium transition ${
              lang === "mr"
                ? "bg-accent-solid text-white"
                : "text-ink-muted hover:text-ink"
            }`}
          >
            मराठी
          </button>
        </div>

        {/* User account dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded px-1.5 py-1 transition hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
            >
              {/* Avatar initials circle */}
              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-solid font-display text-xs font-bold text-white ring-2 ring-signal/70 ring-offset-1 ring-offset-surface">
                {initials}
              </span>
              <span className="hidden text-left sm:block">
                <span className="block text-xs font-medium leading-tight text-ink">
                  {user?.full_name || user?.username || "—"}
                </span>
                <span className="block text-2xs capitalize leading-tight text-ink-muted">
                  {user?.role ?? ""}
                </span>
              </span>
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel className="font-normal">
              <p className="truncate text-xs font-medium text-ink">{user?.username}</p>
              <p className="truncate font-mono text-2xs text-ink-muted">id: {user?.id}</p>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={logout}
              className="text-status-text-critical focus:bg-status-critical/10 focus:text-status-text-critical"
            >
              <LogOut className="mr-2 h-3.5 w-3.5" />
              {t.topBar.signOut}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

